import React, { useEffect, useState } from 'react';
import { apiClient } from '../../lib/api';
import { LeadListMember, ProjectCopyVariant, ProjectLeadList } from '../../types/pipeline';
import { formatRelativeTime } from '../../utils/dateUtils';
import { Badge, Spinner } from '../ui';

/**
 * SKY-540 — where the ball is. The client's decisions (lead-list keep/cut +
 * approval, copy approve/reject with their reason) mirrored onto the operator's
 * board, so "what did the client decide, and why" stops requiring a login to the
 * client portal. Read-only by design: every write stays on the client's own
 * review surface.
 */

/** Newest version per track — approval never carries across versions, so an older
 * row's decision would misreport the track's actual state. */
const latestPerTrack = (variants: ProjectCopyVariant[]): ProjectCopyVariant[] => {
  const byTrack = new Map<string, ProjectCopyVariant>();
  for (const v of variants) {
    const seen = byTrack.get(v.track);
    if (!seen || v.version > seen.version) byTrack.set(v.track, v);
  }
  return [...byTrack.values()].sort((a, b) => a.track.localeCompare(b.track));
};

const DECISION_VARIANT: Record<string, 'success' | 'error' | 'warning' | 'neutral'> = {
  approve: 'success',
  reject: 'error',
  edit: 'warning',
};

const DISPOSITION_VARIANT: Record<string, 'success' | 'error' | 'neutral'> = {
  keep: 'success',
  cut: 'error',
  undecided: 'neutral',
};

const CRM_BUCKET_VARIANT: Record<string, 'success' | 'error' | 'warning' | 'neutral'> = {
  customer: 'error',
  open_opp: 'error',
  closed_lost: 'warning',
  prospect: 'neutral',
};

const CRM_BUCKET_LABEL: Record<string, string> = {
  customer: 'Customer',
  open_opp: 'Open deal',
  closed_lost: 'Closed lost',
  prospect: 'Prospect',
};

/** SKY-544: the pre-screen — who is actually on the list, and what the client
 * did with each row. Fetched only when opened; a list can hold hundreds and the
 * panel is a glance surface first. */
const MemberRows: React.FC<{ projectId: string; listId: string }> = ({ projectId, listId }) => {
  const [rows, setRows] = useState<LeadListMember[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getProjectLeadListMembers(projectId, listId, { limit: 100 })
      .then((res) => !cancelled && setRows(res))
      .catch((error) => {
        if (cancelled) return;
        console.error('Lead list members fetch failed:', error);
        setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, listId]);

  if (failed) {
    return (
      <p className="text-2xs text-error-600 mt-1.5" role="alert">
        Couldn&apos;t load these people.
      </p>
    );
  }
  if (rows === null) return <Spinner size="sm" />;
  if (!rows.length) return <p className="text-2xs text-neutral-500 mt-1.5">This list is empty.</p>;

  return (
    <ul className="mt-1.5 space-y-1 max-h-56 overflow-auto">
      {rows.map((m) => (
        <li key={m.entity_id} className="flex items-baseline justify-between gap-2 text-2xs">
          <span className="truncate text-neutral-800">
            {m.name}
            {m.title || m.company ? (
              <span className="text-neutral-500">
                {' — '}
                {[m.title, m.company].filter(Boolean).join(', ')}
              </span>
            ) : null}
            {/* v0 sourcing leaves the email blank unless the KG already carried a
                verified one — say so rather than implying the person is sendable. */}
            {!m.email && <span className="text-neutral-400"> · no verified email</span>}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {m.crm_bucket && CRM_BUCKET_LABEL[m.crm_bucket] && (
              <Badge
                variant={CRM_BUCKET_VARIANT[m.crm_bucket]}
                size="sm"
                aria-label={`CRM state ${CRM_BUCKET_LABEL[m.crm_bucket]}`}
              >
                {CRM_BUCKET_LABEL[m.crm_bucket]}
              </Badge>
            )}
            <Badge variant={DISPOSITION_VARIANT[m.disposition ?? 'undecided'] ?? 'neutral'} size="sm">
              {m.disposition ?? 'undecided'}
            </Badge>
          </span>
        </li>
      ))}
      {rows.length === 100 && (
        <li className="text-2xs text-neutral-400">First 100 shown.</li>
      )}
    </ul>
  );
};

const ListCard: React.FC<{
  list: ProjectLeadList;
  projectId: string;
  open: boolean;
  onToggle: () => void;
}> = ({ list, projectId, open, onToggle }) => (
  <li className="border border-neutral-200 rounded p-2">
    <div className="flex items-baseline justify-between gap-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="text-xs font-medium text-neutral-900 truncate text-left hover:text-primary-600"
      >
        {open ? '▾ ' : '▸ '}
        {list.name}
      </button>
      {list.approved_at ? (
        <Badge variant="success" size="sm">
          approved
        </Badge>
      ) : (
        <Badge variant="warning" size="sm">
          awaiting client
        </Badge>
      )}
    </div>
    <p className="text-2xs text-neutral-600 mt-1 tabular-nums">
      {list.kept_count} kept · {list.cut_count} cut · {list.undecided_count} undecided
      <span className="text-neutral-400"> of {list.member_count}</span>
    </p>
    <p className="text-2xs text-neutral-500 mt-0.5">
      {list.approved_at
        ? `approved ${formatRelativeTime(list.approved_at)}`
        : list.created_at
          ? `saved ${formatRelativeTime(list.created_at)}`
          : null}
      {' · '}
      <a
        href={`/portal/campaigns/${projectId}?tab=audience`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary-600 hover:underline"
      >
        client's review surface
      </a>
    </p>
    {open && <MemberRows projectId={projectId} listId={list.id} />}
  </li>
);

const CopyRow: React.FC<{ variant: ProjectCopyVariant }> = ({ variant }) => (
  <li className="border border-neutral-200 rounded p-2">
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs font-medium text-neutral-900 truncate">
        {variant.track} · v{variant.version}
      </span>
      {variant.decision ? (
        <Badge variant={DECISION_VARIANT[variant.decision] ?? 'neutral'} size="sm">
          {variant.decision === 'approve' ? 'approved' : variant.decision}
        </Badge>
      ) : (
        <Badge variant="warning" size="sm">
          awaiting review
        </Badge>
      )}
    </div>
    {/* The reason is the whole point of the mirror — an operator who can see
        "rejected" but not why still has to go ask. */}
    {variant.decision_reason && (
      <p className="text-2xs text-neutral-600 mt-1 break-words">“{variant.decision_reason}”</p>
    )}
    {variant.decided_at && (
      <p className="text-2xs text-neutral-500 mt-0.5">
        decided {formatRelativeTime(variant.decided_at)}
      </p>
    )}
  </li>
);

export const DecisionMirror: React.FC<{
  projectId: string;
  refreshKey?: number;
  /** A lead_list artifact clicked in the runs panel opens that list here (SKY-544). */
  focusListId?: string | null;
}> = ({ projectId, refreshKey = 0, focusListId = null }) => {
  const [lists, setLists] = useState<ProjectLeadList[]>([]);
  const [copy, setCopy] = useState<ProjectCopyVariant[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [openListId, setOpenListId] = useState<string | null>(null);

  // A run's artifact is the operator's entry point into the list it produced.
  useEffect(() => {
    if (focusListId) setOpenListId(focusListId);
  }, [focusListId]);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    // Settled, not all: one dead surface must not blank the other — a project
    // can legitimately have lists and no copy, or copy and no list.
    Promise.allSettled([
      apiClient.getProjectLeadLists(projectId),
      apiClient.getProjectCopy(projectId),
    ])
      .then(([listRes, copyRes]) => {
        if (cancelled) return;
        if (listRes.status === 'rejected' && copyRes.status === 'rejected') {
          console.error('Decision mirror fetch failed:', listRes.reason, copyRes.reason);
          setState('error');
          return;
        }
        setLists(listRes.status === 'fulfilled' ? listRes.value : []);
        setCopy(copyRes.status === 'fulfilled' ? copyRes.value : []);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, refreshKey]);

  if (state === 'loading') {
    return (
      <div>
        <h4 className="text-13 font-semibold text-neutral-700 mb-1">Client decisions</h4>
        <Spinner size="sm" />
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div>
        <h4 className="text-13 font-semibold text-neutral-700 mb-1">Client decisions</h4>
        <p className="text-xs text-error-600" role="alert">
          Couldn&apos;t load the client&apos;s decisions — reopen this panel to retry.
        </p>
      </div>
    );
  }

  const tracks = latestPerTrack(copy);
  if (!lists.length && !tracks.length) {
    return (
      <div>
        <h4 className="text-13 font-semibold text-neutral-700 mb-1">Client decisions</h4>
        <p className="text-xs text-neutral-500">
          Nothing has been sent to the client for review on this project yet.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-13 font-semibold text-neutral-700 mb-1">Client decisions</h4>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <p className="text-2xs uppercase tracking-wide text-neutral-500 mb-1">Lead lists</p>
          {lists.length ? (
            <ul className="space-y-1.5">
              {lists.map(l => (
                <ListCard
                  key={l.id}
                  list={l}
                  projectId={projectId}
                  open={openListId === l.id}
                  onToggle={() => setOpenListId(id => (id === l.id ? null : l.id))}
                />
              ))}
            </ul>
          ) : (
            <p className="text-xs text-neutral-500">No list saved yet.</p>
          )}
        </div>
        <div>
          <p className="text-2xs uppercase tracking-wide text-neutral-500 mb-1">Copy</p>
          {tracks.length ? (
            <ul className="space-y-1.5">
              {tracks.map(v => (
                <CopyRow key={v.id} variant={v} />
              ))}
            </ul>
          ) : (
            <p className="text-xs text-neutral-500">No copy uploaded yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};
